import { Injectable } from '@angular/core';
import { ReservingService } from '@plys/src/infrastructure/reserving.service';
import { BehaviorSubject, combineLatestWith, filter, map, Observable, tap } from 'rxjs';

import { ReservationType, ReservationUserData } from '../../models/reservation';

export interface ReservationTypeMap {
  restaurantId: string;
  type: ReservationType;
}

@Injectable({
  providedIn: 'root',
})
export class ReservationState {
  private readonly types = new BehaviorSubject<ReservationTypeMap[]>([]);
  readonly availability = new BehaviorSubject<Map<string, [Date[], Date[]]>>(new Map());
  private readonly reservationUserData = new BehaviorSubject<ReservationUserData>(null);
  private readonly cvCards = new BehaviorSubject<CvCardStateEl[]>([]);
  private readonly subscriptionCards = new BehaviorSubject<SubscriptionCardStateEl[]>([]);

  constructor(private reservingService: ReservingService) {}

  get reservationUserData$(): Observable<ReservationUserData> {
    return this.reservationUserData.asObservable();
  }

  /** Synchronous read for route guards, which have to answer before the navigation resolves. */
  getReservationUserDataValue(): ReservationUserData | null {
    return this.reservationUserData.value;
  }

  get cvCards$(): Observable<CvCardStateEl[]> {
    return this.cvCards.asObservable();
  }

  get subscriptionCards$(): Observable<SubscriptionCardStateEl[]> {
    return this.subscriptionCards.asObservable();
  }

  getSubscriptionCardsValue(): SubscriptionCardStateEl[] {
    return this.subscriptionCards.value;
  }

  get availability$(): Observable<Date[]> {
    return this.availability.pipe(
      filter((availabilityMap) => !!availabilityMap && availabilityMap.size > 0),
      map((availabilityMap) => {
        const allDates: Date[] = [];
        availabilityMap.forEach(([before, after]) => {
          allDates.push(...before, ...after);
        });
        return allDates;
      })
    );
  }

  getAvailabilityForRestaurant(restaurantId: string): Observable<Date[]> {
    return this.types.pipe(
      combineLatestWith(this.availability),
      filter(([types, availabilityMap]) => !!availabilityMap && availabilityMap.size > 0),
      map(([types, availabilityMap]) => {
        const restaurantType = types.find((t) => t.restaurantId === restaurantId);
        const type = restaurantType?.type || 'before';
        const restaurantAvailability = availabilityMap.get(restaurantId);
        if (!restaurantAvailability) return [];
        return type === 'before' ? restaurantAvailability[0] : restaurantAvailability[1];
      })
    );
  }

  changeType(restaurantId: string, type: ReservationType): void {
    const current = this.types.getValue();
    const existing = current.find((t) => t.restaurantId === restaurantId);
    if (existing) {
      existing.type = type;
      this.types.next([...current]);
    } else {
      this.types.next([...current, { restaurantId, type }]);
    }
  }

  getTypeForRestaurant(restaurantId: string): ReservationType {
    const types = this.types.getValue();
    return types.find((t) => t.restaurantId === restaurantId)?.type || 'before';
  }

  setReservationUserData(userData: ReservationUserData): void {
    this.reservationUserData.next(userData);
  }

  /**
   * Stores a buyer who has no account, keyed by the cart they are buying. A logged-in buyer is
   * restored on every app boot (AppComponent's `getUser()`), but a guest exists nowhere outside this
   * in-memory state: without this, a refresh anywhere after the account step would leave the order
   * with no buyer to create it for, and the step guard would send them back to re-enter it.
   * Session-scoped and cart-scoped, so it can neither outlive the tab nor leak into another cart.
   */
  saveGuestBuyer(cartId: string, userData: ReservationUserData): void {
    this.setReservationUserData(userData);
    if (!cartId) {
      return;
    }
    sessionStorage.setItem(this.guestBuyerKey(cartId), JSON.stringify(userData));
  }

  /**
   * Restores the guest buyer of `cartId` after a refresh. A buyer already in memory always wins —
   * it is either the logged-in user or the same data this would read back. Returns whether a buyer
   * is known afterwards.
   */
  restoreGuestBuyer(cartId: string): boolean {
    if (this.reservationUserData.value) {
      return true;
    }
    if (!cartId) {
      return false;
    }
    const stored = sessionStorage.getItem(this.guestBuyerKey(cartId));
    if (!stored) {
      return false;
    }
    try {
      this.setReservationUserData(JSON.parse(stored) as ReservationUserData);
      return true;
    } catch {
      // Unreadable leftover from an older shape: drop it rather than break the bootstrap.
      sessionStorage.removeItem(this.guestBuyerKey(cartId));
      return false;
    }
  }

  /**
   * Drops the stored guest buyer of `cartId`. Called when the same cart gets a logged-in buyer: the
   * leftover would otherwise win after a refresh, because it is restored synchronously with the cart
   * while the account is only restored once `/me` lands — and the order would be placed under the
   * guest identity instead of the account.
   */
  clearGuestBuyer(cartId: string): void {
    if (!cartId) {
      return;
    }
    sessionStorage.removeItem(this.guestBuyerKey(cartId));
  }

  private guestBuyerKey(cartId: string): string {
    return `plys_guest_buyer_${cartId}`;
  }

  addCvCard(cvCard: CvCardStateEl): void {
    const update = this.cvCards.getValue().filter((card) => card.cartLineId !== cvCard.cartLineId);
    update.push(cvCard);
    this.cvCards.next(update);
  }

  /**
   * Points the lines redeemed with `barcode` at the account card it was just saved as, so a step
   * rebuilt from this state shows that card picked rather than a typed "new card".
   */
  markCvCardSaved(barcode: string, name: string): void {
    this.cvCards.next(
      this.cvCards.getValue().map((card) => (card.barcode === barcode ? { ...card, cardId: barcode, name } : card))
    );
  }

  addSubscriptionCard(subscriptionCard: SubscriptionCardStateEl): void {
    const update = this.subscriptionCards.getValue().filter((card) => card.cartLineId !== subscriptionCard.cartLineId);
    update.push(subscriptionCard);
    this.subscriptionCards.next(update);
  }

  /** Drops the card of one line — the buyer detached it, or the attach was refused. */
  removeSubscriptionCard(cartLineId: string): void {
    this.subscriptionCards.next(this.subscriptionCards.getValue().filter((card) => card.cartLineId !== cartLineId));
  }

  removeSubscriptionCards(): void {
    this.subscriptionCards.next([]);
  }

  /**
   * Persists the card↔line mapping for `cartId`. The line itself echoes `subscription_card_code`
   * back, but not the saved card it was picked from nor the label shown next to the row, and that
   * only lives here. Session- and cart-scoped, following {@link saveGuestBuyer}.
   */
  saveSubscriptionCards(cartId: string): void {
    if (!cartId) {
      return;
    }
    sessionStorage.setItem(this.subscriptionCardsKey(cartId), JSON.stringify(this.subscriptionCards.value));
  }

  /** Restores the persisted card↔line mapping of `cartId` after a refresh. In-memory state wins. */
  restoreSubscriptionCards(cartId: string): void {
    if (!cartId || this.subscriptionCards.value.length > 0) {
      return;
    }
    const stored = sessionStorage.getItem(this.subscriptionCardsKey(cartId));
    if (!stored) {
      return;
    }
    try {
      this.subscriptionCards.next(JSON.parse(stored) as SubscriptionCardStateEl[]);
    } catch {
      // Unreadable leftover from an older shape: drop it rather than break the bootstrap.
      sessionStorage.removeItem(this.subscriptionCardsKey(cartId));
    }
  }

  private subscriptionCardsKey(cartId: string): string {
    return `plys_sub_cards_${cartId}`;
  }

  fetchAvailability(showStart: Date, showEnd: Date, restaurant_reservation_id: string): Observable<Date[]> {
    return this.reservingService.getAvailability(restaurant_reservation_id).pipe(
      tap((availability) => {
        const beforeShow = availability.filter((date) => date < showStart).sort((a, b) => b.getTime() - a.getTime());
        const afterShow = availability.filter((date) => date >= showEnd).sort((a, b) => a.getTime() - b.getTime());
        const current = new Map(this.availability.getValue());
        current.set(restaurant_reservation_id, [beforeShow, afterShow]);
        this.availability.next(current);
      })
    );
  }
}

export interface CvCardStateEl {
  cartLineId: string;
  barcode: string;
  name?: string;
  cardId?: string;
}

/** A card the buyer has redeemed on a line: an entry exists only while the attach holds. */
export interface SubscriptionCardStateEl {
  cartLineId: string;
  code: string;
  name?: string;
  cardId?: string;
}
